import { create } from "@bufbuild/protobuf";
import { Dialog } from "@radix-ui/react-dialog";
import { CheckIcon, Loader2 } from "lucide-react";
import type React from "react";
import { useCallback, useMemo, useState, type FC } from "react";
import { Link } from "react-router-dom";

import { IconWarning } from "@/assets/icons/geist/IconWarning";
import { AppleLogo } from "@/assets/logos/AppleLogo";
import { AuthenticationModal } from "@/components/environments/onboarding/CreateProjectFromEnvironmentModal";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { StatusDot, type StatusDotColor } from "@/components/flexkit/status-dot/StatusDot";
import { DataTable, type ColumnDef } from "@/components/flexkit/tables/DataTable";
import type { MobileRowDescriptor } from "@/components/flexkit/tables/DataTableMobileView";
import { useToast } from "@/components/flexkit/toast";
import { ExternalLink } from "@/components/flexkit/typography/ExternalLink";
import { Text } from "@/components/flexkit/typography/Text";
import {
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import type { PillVariant } from "@/components/podkit/status/Pill";
import { SourceControlProviderIcon } from "@/components/runners/details/SourceControlProviderIcon";
import { getPhaseInfo } from "@/components/runners/runner-labels";
import { Tooltip } from "@/components/Tooltip";
import { DESKTOP_APP_DOWNLOAD_URL } from "@/desktop";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { TrackLocations } from "@/hooks/use-segment";
import { toPlainRunnerEnvironmentClass, useRunnerEnvironments } from "@/queries/environment-queries";
import {
    useDeleteHostAuthenticationToken,
    useListAllRunnerSCMIntegrations,
    useListHostAuthenticationTokens,
} from "@/queries/runner-configuration-queries";
import {
    useIsUserAuthenticatedWithRunner,
    useListRunners,
    type AuthenticatedWithRunnerResponse,
} from "@/queries/runner-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { formatError } from "@/utils/errors";
import { hostAuthenticationTokenSourceLabel } from "@/utils/host-authentication-token-source";
import {
    type HostAuthenticationTokenSource,
    RunnerKind,
    type HostAuthenticationToken,
    type SCMIntegration,
    EnvironmentClassSchema,
} from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { RunnerPhase, type Runner } from "gitpod-next-api/gitpod/v1/runner_pb";

type RunnerTokenBase = {
    entryId: string;
    scmHost: string;
    runner: Runner;
};
type RunnerTokenLocal = RunnerTokenBase & {
    token: HostAuthenticationToken;
    integration: undefined;
};

type RunnerTokenRemote = RunnerTokenBase & {
    token: HostAuthenticationToken | undefined;
    integration: SCMIntegration;
};

type RunnerToken = RunnerTokenLocal | RunnerTokenRemote;

export type GitAuthenticationsPageProps = {
    /** When true, hides the description text (used in modal context where description is provided externally) */
    hideDescription?: boolean;
};

// ── Status dot helpers ──────────────────────────────────────────────

const pillVariantToStatusDotColor: Record<NonNullable<PillVariant>, StatusDotColor> = {
    success: "green",
    warning: "orange",
    danger: "red",
    default: "gray",
    info: "gray",
    neutral: "gray",
    brand: "brand",
    "brand-2": "brand",
};

function runnerStatusDotColor(runner: Runner): StatusDotColor {
    const info = getPhaseInfo(runner);
    return pillVariantToStatusDotColor[info.variant ?? "default"];
}

function runnerStatusLabel(runner: Runner): string {
    return getPhaseInfo(runner).label;
}

function isRunnerReachable(runner: Runner): boolean {
    const phase = runner.status?.phase ?? RunnerPhase.UNSPECIFIED;
    return phase === RunnerPhase.ACTIVE || phase === RunnerPhase.DEGRADED;
}

// ── Page component ──────────────────────────────────────────────────

export const GitAuthenticationsPage: FC<GitAuthenticationsPageProps> = ({ hideDescription = false }) => {
    useDocumentTitle("Git Authentications");
    const { data: tokensData, error: tokensError, isPending: isPendingTokens } = useListHostAuthenticationTokens({});
    const { data: runners, isPending: isPendingRunners } = useListRunners({ kind: RunnerKind.REMOTE });
    const { isPending: isPendingUser } = useAuthenticatedUser();

    const { data: integrations, isPending: integrationsPending } = useListAllRunnerSCMIntegrations(
        (runners || []).map((runner) => runner.runnerId),
    );

    const tokens: RunnerToken[] = useMemo(
        () => runnerTokens(tokensData?.tokens || [], runners || [], integrations || []),
        [tokensData, runners, integrations],
    );

    const isLoading = isPendingTokens || isPendingRunners || isPendingUser || integrationsPending;

    if (isLoading) {
        return <LoadingState />;
    }

    return (
        <div data-testid="git-authentications">
            <ErrorMessage error={tokensError} />
            <div className={hideDescription ? "" : "flex flex-col gap-6"}>
                {!hideDescription && (
                    <Text className="text-base text-content-secondary">
                        Git authentication connects Ona to your source control accounts. This page shows your current
                        GitHub, Bitbucket Cloud, or Azure DevOps connections for both runners and Ona Desktop.
                    </Text>
                )}
                {!tokensError && (
                    <>
                        {tokens.length === 0 && (
                            <EmptyStateCard
                                title="No Git authentications"
                                description="You need to add a runner or install Ona Desktop before connecting to your source control accounts."
                                data-testid="no-runner-configured-content"
                            >
                                <Button variant="secondary" asChild>
                                    <Link to="/settings/runners" data-testid="runner-onboarding-link">
                                        Set up a Runner
                                    </Link>
                                </Button>

                                <Button variant="secondary" asChild LeadingIcon={AppleLogo}>
                                    <ExternalLink
                                        href={DESKTOP_APP_DOWNLOAD_URL}
                                        download
                                        data-testid="runner-onboarding-link"
                                    >
                                        Download Ona Desktop
                                    </ExternalLink>
                                </Button>
                            </EmptyStateCard>
                        )}
                        {tokens.length > 0 && <GitAuthenticationsTable tokens={tokens} />}
                    </>
                )}
            </div>
        </div>
    );
};

// ── DataTable implementation ────────────────────────────────────────

const GitAuthenticationsTable: FC<{ tokens: RunnerToken[] }> = ({ tokens }) => {
    const [deleteToken, setDeleteToken] = useState<RunnerToken | null>(null);
    const [authenticateToken, setAuthenticateToken] = useState<RunnerToken | null>(null);
    const [authCheckResponse, setAuthCheckResponse] = useState<AuthenticatedWithRunnerResponse | null>(null);

    const columns = useMemo<ColumnDef<RunnerToken>[]>(
        () => [
            {
                id: "runner",
                header: "Runner",
                isRowHeader: true,
                cell: (rt) => <RunnerCell runner={rt.runner} authType={rt.token?.source} />,
            },
            {
                id: "gitProvider",
                header: "Git provider",
                cell: (rt) => (
                    <div className="flex min-w-0 items-center gap-2">
                        <SourceControlProviderIcon scmId={rt.integration?.scmId} size="sm" />
                        <span className="truncate text-base" title={rt.scmHost}>
                            {rt.scmHost}
                        </span>
                    </div>
                ),
            },
        ],
        [],
    );

    const handleConnect = useCallback((rt: RunnerToken, response: AuthenticatedWithRunnerResponse) => {
        setAuthCheckResponse(response);
        setAuthenticateToken(rt);
    }, []);

    const inlineActions = useCallback(
        (rt: RunnerToken) => <TokenActionButton token={rt} onDelete={setDeleteToken} onConnect={handleConnect} />,
        [handleConnect],
    );

    const mobileRow = useCallback(
        (rt: RunnerToken): MobileRowDescriptor => ({
            title: (
                <div className="flex min-w-0 items-center gap-2">
                    <StatusDot
                        size="sm"
                        color={runnerStatusDotColor(rt.runner)}
                        tooltip={runnerStatusLabel(rt.runner)}
                    />
                    <span className="truncate font-medium text-content-primary">{rt.runner.name}</span>
                </div>
            ),
            description: (
                <div className="flex items-center gap-1.5">
                    <SourceControlProviderIcon scmId={rt.integration?.scmId} size="sm" />
                    <span className="truncate">{rt.scmHost}</span>
                </div>
            ),
        }),
        [],
    );

    return (
        <>
            <DataTable
                aria-label="Git authentications"
                columns={columns}
                data={tokens}
                getRowId={(rt) => `git-authentications-token-${rt.entryId}`}
                getRowTextValue={(rt) => `${rt.runner.name} ${rt.scmHost}`}
                inlineActions={inlineActions}
                mobileRow={mobileRow}
            />

            {deleteToken?.token && (
                <HostAuthenticationTokenDeleteModal
                    token={{ ...deleteToken, token: deleteToken.token }}
                    onClose={() => setDeleteToken(null)}
                />
            )}
            {authenticateToken && authCheckResponse && (
                <HostAuthenticationTokenAuthenticateModal
                    token={authenticateToken}
                    authCheckResponse={authCheckResponse}
                    autoConnect={authCheckResponse.type === "AuthenticationRequired" && !authCheckResponse.patAuth}
                    onClose={() => {
                        setAuthenticateToken(null);
                        setAuthCheckResponse(null);
                    }}
                />
            )}
        </>
    );
};

// ── Runner cell with status dot ─────────────────────────────────────

const RunnerCell: FC<{ runner: Runner; authType?: HostAuthenticationTokenSource }> = ({ runner, authType }) => {
    const statusLabel = runnerStatusLabel(runner);
    const tooltipParts = [runner.name, statusLabel];
    if (authType !== undefined) {
        tooltipParts.push(hostAuthenticationTokenSourceLabel(authType));
    }

    return (
        <Tooltip content={tooltipParts.join(" · ")} usePortal>
            <div className="flex min-w-0 items-center gap-2">
                <StatusDot size="sm" color={runnerStatusDotColor(runner)} className="shrink-0" />
                <span className="truncate text-base font-medium">{runner.name}</span>
            </div>
        </Tooltip>
    );
};

// ── Inline action button (Connect / Disconnect) ────────────────────

const TokenActionButton: FC<{
    token: RunnerToken;
    onDelete: (rt: RunnerToken) => void;
    onConnect: (rt: RunnerToken, response: AuthenticatedWithRunnerResponse) => void;
}> = ({ token, onDelete, onConnect }) => {
    const { isLoading: isAuthCheckLoading, refetch: refetchAuthCheck } = useIsUserAuthenticatedWithRunner(
        token.runner.runnerId,
        "https://" + token.scmHost,
        {
            refetchUntilAuthenticated: false,
            enabled: true,
        },
    );

    const handleConnectClick = useCallback(async () => {
        const result = await refetchAuthCheck();
        if (result.data) {
            onConnect(token, result.data);
        }
    }, [refetchAuthCheck, onConnect, token]);

    if (token.token) {
        return (
            <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(token)}
                data-testid={`git-authentications-delete-token-${token.entryId}`}
                data-tracking-id="disconnect-git-host-authentication-token"
            >
                Disconnect
            </Button>
        );
    }

    const runnerOffline = !isRunnerReachable(token.runner);

    return (
        <Button
            variant="text"
            size="sm"
            onClick={handleConnectClick}
            disabled={isAuthCheckLoading || runnerOffline}
            data-testid={`git-authentications-create-token-${token.entryId}`}
            data-tracking-id="connect-git-host-authentication-token"
        >
            {isAuthCheckLoading ? "Checking..." : "Connect"}
        </Button>
    );
};

// ── Modals (unchanged) ──────────────────────────────────────────────

const HostAuthenticationTokenAuthenticateModal: FC<{
    token: RunnerToken;
    authCheckResponse: AuthenticatedWithRunnerResponse;
    onClose: () => void;
    autoConnect?: boolean;
}> = ({ token, onClose, authCheckResponse, autoConnect }) => {
    const { toast } = useToast();

    const clazz = useMemo(() => {
        return toPlainRunnerEnvironmentClass(
            create(EnvironmentClassSchema, { displayName: "", runnerId: token.runner.runnerId }),
        );
    }, [token.runner.runnerId]);

    return (
        <AuthenticationModal
            repoURL={"https://" + token.scmHost}
            authResponse={authCheckResponse}
            runnerId={clazz.runnerId}
            onAuthSuccess={() => {
                toast({ title: "Authentication successful" });
                onClose();
            }}
            onClose={() => {
                onClose();
            }}
            data-track-location={TrackLocations.GitAuthenticationTokenCreateModal}
            autoConnect={autoConnect}
        />
    );
};

const HostAuthenticationTokenDeleteModal: FC<{
    token: RunnerToken & { token: NonNullable<RunnerToken["token"]> };
    onClose: () => void;
}> = ({ token, onClose }) => {
    const { toast } = useToast();
    const deleteToken = useDeleteHostAuthenticationToken();
    const listEnvironments = useRunnerEnvironments(token.token.runnerId);

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
        }
    };

    const handleDeleteToken = useCallback(() => {
        deleteToken.mutate(
            { tokenId: token.token.id },
            {
                onSuccess: () => {
                    toast({ title: "Authentication removed" });
                    onClose();
                },
                onError: (err) => {
                    toast({ title: "Failed to remove authentication", description: formatError(err) });
                },
            },
        );
    }, [deleteToken, onClose, token, toast]);

    return (
        <Dialog open onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-[400px]"
                data-testid={`git-authentications-delete-token-modal-${token.entryId}`}
                data-track-location={TrackLocations.GitAuthenticationTokenDeleteModal}
            >
                <DialogHeader>
                    <DialogTitle>Remove authentication</DialogTitle>
                    <DialogDescription />
                </DialogHeader>
                <DialogBody className="flex flex-col gap-4">
                    <Text>
                        Remove your access to {token.token.host} for{" "}
                        <span className="font-bold">{token.runner.name}</span> runner?
                    </Text>
                    <div className="mb-1 flex rounded-lg border border-border-light bg-surface-secondary p-4 text-content-secondary">
                        <AffectedEnvironmentsText
                            token={token}
                            isLoading={listEnvironments.isLoading || listEnvironments.isPending}
                            environmentCount={listEnvironments.data?.environments.length ?? 0}
                            error={listEnvironments.error}
                        />
                    </div>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            data-tracking-id="cancel-remove-git-authentication-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        autoFocus={true}
                        variant="destructive"
                        disabled={listEnvironments.isLoading || listEnvironments.isPending}
                        loading={deleteToken.isPending}
                        onClick={handleDeleteToken}
                        data-testid={`git-authentications-delete-token-modal-${token.entryId}-remove`}
                        data-tracking-id="confirm-remove-git-authentication-modal"
                    >
                        Yes, Remove
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const AffectedEnvironmentsText: FC<{
    token: RunnerToken;
    isLoading: boolean;
    environmentCount: number;
    error: Error | null;
}> = ({ token, isLoading, environmentCount, error }) => {
    let icon: React.ReactNode;
    let content: string;
    const hostName = token.token?.host || token.scmHost;

    if (isLoading) {
        icon = <Loader2 strokeWidth={2} className="animate-spin text-content-yield" size={20} />;
        content = "Verifying affected environments...";
    } else if (error) {
        icon = <IconWarning size={"base"} className="text-content-negative" />;
        content = `There was an error while verifying affected environments. Proceed with caution. Any existing environments will be unable to communicate with ${hostName} until you re-authenticate.`;
    } else if (environmentCount == 0) {
        icon = <CheckIcon size={20} className="text-content-positive" />;
        content = "Safe to remove.";
    } else {
        icon = <IconWarning size={"base"} className="text-content-yield" />;
        content = `${environmentCount} environment${environmentCount > 1 ? "s" : ""} will be unable to communicate with ${hostName} until you re-authenticate.`;
    }

    return (
        <div className="inline-flex items-start justify-start gap-2">
            <div className="flex size-5 items-center">{icon}</div>
            <Text className="text-content-secondary">{content}</Text>
        </div>
    );
};

// ── Data helpers ────────────────────────────────────────────────────

function runnerTokens(
    tokens: HostAuthenticationToken[],
    runners: Runner[],
    integrations: SCMIntegration[],
): RunnerToken[] {
    const runnerById = Object.fromEntries(runners.map((runner) => [runner.runnerId, runner]));
    const tokenByRunnerIdAndHost = Object.fromEntries(
        tokens.map((token) => [`${token.runnerId}-${token.host}`, token]),
    );
    const integrationByRunnerIdAndHost = Object.fromEntries(
        integrations.map((integration) => [`${integration.runnerId}-${integration.host}`, integration]),
    );
    // Add remote runner tokens, they're re-authenticatable
    const values: RunnerToken[] = [];
    for (const integration of integrations) {
        const runner = runnerById[integration.runnerId] || undefined;
        const token = tokenByRunnerIdAndHost[`${integration.runnerId}-${integration.host}`] || undefined;
        if (runner) {
            values.push({
                entryId: integration.id,
                scmHost: integration.host,
                token,
                runner,
                integration,
            });
        }
    }
    // Add local runner tokens, not re-authenticatable on git authentications settings page
    for (const token of tokens) {
        const runner = runnerById[token.runnerId] || undefined;
        if (!runner || runner.kind === RunnerKind.REMOTE) {
            continue;
        }
        const integration = integrationByRunnerIdAndHost[`${token.runnerId}-${token.host}`] || undefined;
        values.push({
            entryId: token.id,
            scmHost: token.host,
            token,
            runner,
            integration,
        });
    }
    return values;
}
